import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cartService } from '@/services/cart';
import type {
  AddToCartResult,
  RemoveFromCartResult,
  ClearCartResult,
  GetCartResult,
  WorkflowType,
} from '@/services/cart';
import { useNotifications } from '@/stores/useNotificationStore';

export type TransfersShippingWorkflowType = Extract<WorkflowType, 'transfer' | 'shipment'>;

const DEFAULT_WORKFLOW_TYPE: TransfersShippingWorkflowType = 'transfer';

export const transfersShippingCartQueryKeys = {
  all: ['transfers-shipping-cart'] as const,
  cart: (userId: string | null | undefined, workflowType: TransfersShippingWorkflowType) =>
    [...transfersShippingCartQueryKeys.all, 'cart', workflowType, userId ?? 'no-user'] as const,
};

export const useTransfersShippingCart = (
  userId: string | null | undefined,
  workflowType: TransfersShippingWorkflowType = DEFAULT_WORKFLOW_TYPE,
  options?: {
    enabled?: boolean;
  },
) => {
  return useQuery<GetCartResult>({
    queryKey: transfersShippingCartQueryKeys.cart(userId, workflowType),
    queryFn: () => cartService.getCart(userId!, workflowType),
    enabled: options?.enabled !== false && !!userId,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
};

export const useAddToTransfersShippingCart = (
  workflowType: TransfersShippingWorkflowType = DEFAULT_WORKFLOW_TYPE,
) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<AddToCartResult, Error, { userId: string; assetIds: string[] }>({
    mutationFn: (params) =>
      cartService.addToCart({
        customerId: params.userId,
        assetIds: params.assetIds,
        itemSource: 'additional',
        workflowType,
      }),
    onSuccess: (result, variables) => {
      if (!result.success) {
        if (result.already_reserved && result.already_reserved.length > 0) {
          notifications.warning(
            'Assets unavailable',
            `${result.already_reserved.length} asset(s) are already reserved by another user or workflow.`,
          );
        } else {
          notifications.error('Failed to add to cart', result.error ?? 'Unknown error');
        }
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: transfersShippingCartQueryKeys.cart(variables.userId, workflowType),
      });

      void queryClient.invalidateQueries({ queryKey: ['asset-inventory'] });
    },
    onError: (error) => {
      notifications.error('Failed to add to cart', error.message);
    },
  });
};

export const useRemoveFromTransfersShippingCart = (
  workflowType: TransfersShippingWorkflowType = DEFAULT_WORKFLOW_TYPE,
) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation<RemoveFromCartResult, Error, { userId: string; assetIds: string[] }>({
    mutationFn: (params) =>
      cartService.removeFromCart({
        customerId: params.userId,
        assetIds: params.assetIds,
        workflowType,
      }),
    onSuccess: async (result, variables) => {
      if (!result.success) {
        notifications.error('Failed to remove from cart', result.error ?? 'Unknown error');
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: transfersShippingCartQueryKeys.cart(variables.userId, workflowType),
      });

      void queryClient.invalidateQueries({ queryKey: ['asset-inventory'] });
    },
    onError: (error) => {
      notifications.error('Failed to remove from cart', error.message);
    },
  });
};

export const useClearTransfersShippingCart = (
  workflowType: TransfersShippingWorkflowType = DEFAULT_WORKFLOW_TYPE,
) => {
  const queryClient = useQueryClient();

  return useMutation<ClearCartResult, Error, { userId: string; cartId?: string }>({
    mutationFn: async ({ userId, cartId }) => {
      const result = await cartService.clearCart(userId, workflowType);

      if (result.success && cartId) {
        try {
          await cartService.deleteCart(cartId);
        } catch {
          // Cart cleanup is best-effort
        }
      }

      return result;
    },
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({
        queryKey: transfersShippingCartQueryKeys.cart(variables.userId, workflowType),
      });

      void queryClient.invalidateQueries({ queryKey: ['asset-inventory'] });
    },
  });
};
